import {
  buildLectureNumberMap,
  COURSE_CATEGORIES,
  sortLectureItems,
} from "./course-categories";
import type { ScoreRow } from "./scores-api";
import { getLearningScore, isCoursePassingScore } from './learning-score';

export interface StudentScenario {
  id: number;
  courseCategoryId?: number | null;
  lectureOrder?: number | null;
  lectureTitle?: string | null;
  guidanceLevel?: "detailed" | "viewpoints" | "purpose_only" | null;
  conditionId: number;
  mode: string;
  summary?: string | null;
  questionnaire?: string | null;
  targetCategories: string[];
  targetCategoryLabels?: Record<string, string>;
  tasks: Array<{ no: number; title: string; details?: string[] }>;
}

export interface StudentScenarioResponse {
  scenarios: StudentScenario[];
}

export interface CourseProgress {
  courseCategoryId: number;
  label: string;
  done: number;
  total: number;
  percent: number;
}

export function getScenarioBestScores(
  scenarioData: StudentScenarioResponse | undefined,
  scores: ScoreRow[] | undefined,
): Map<number, number> {
  const availableScenarioIds = new Set(
    (scenarioData?.scenarios ?? []).map((scenario) => scenario.id),
  );
  const bestScores = new Map<number, number>();
  for (const score of scores ?? []) {
    if (
      typeof score.conditionInstructionId !== "number" ||
      !availableScenarioIds.has(score.conditionInstructionId) ||
      typeof score.overall_score_value !== "number" ||
      !Number.isFinite(score.overall_score_value)
    ) {
      continue;
    }
    const value = getLearningScore(score)!.score;
    const current = bestScores.get(score.conditionInstructionId);
    if (current === undefined || value > current) {
      bestScores.set(score.conditionInstructionId, value);
    }
  }
  return bestScores;
}

export function getCompletedScenarioIds(
  scenarioData: StudentScenarioResponse | undefined,
  scores: ScoreRow[] | undefined,
): Set<number> {
  return new Set(
    Array.from(getScenarioBestScores(scenarioData, scores?.filter(isCoursePassingScore)))
      .filter(([, bestScore]) => bestScore >= 80)
      .map(([scenarioId]) => scenarioId),
  );
}

export function buildCourseProgress(
  scenarioData: StudentScenarioResponse | undefined,
  scores: ScoreRow[] | undefined,
): CourseProgress[] {
  const scenarios = scenarioData?.scenarios ?? [];
  const completedScenarioIds = getCompletedScenarioIds(scenarioData, scores);

  return COURSE_CATEGORIES.map((category) => {
    const courseScenarios = scenarios.filter(
      (scenario) => scenario.courseCategoryId === category.id,
    );
    const done = courseScenarios.filter((scenario) =>
      completedScenarioIds.has(scenario.id),
    ).length;
    const total = courseScenarios.length;
    return {
      courseCategoryId: category.id,
      label: category.name,
      done,
      total,
      percent: total > 0 ? Math.round((done / total) * 100) : 0,
    };
  });
}

export function getRecommendedScenario(
  scenarioData: StudentScenarioResponse | undefined,
  scores: ScoreRow[] | undefined,
): StudentScenario | undefined {
  const completedScenarioIds = getCompletedScenarioIds(scenarioData, scores);
  return sortLectureItems(
    (scenarioData?.scenarios ?? []).map((scenario) => ({
      ...scenario,
      courseCategoryId: scenario.courseCategoryId ?? null,
    })),
  ).find((scenario) => !completedScenarioIds.has(scenario.id));
}

export function getLectureNumber(
  scenarioData: StudentScenarioResponse | undefined,
  scenarioId: number,
): number | undefined {
  return buildLectureNumberMap(
    (scenarioData?.scenarios ?? []).map((scenario) => ({
      id: scenario.id,
      courseCategoryId: scenario.courseCategoryId ?? null,
      lectureOrder: scenario.lectureOrder ?? null,
    })),
  ).get(scenarioId);
}
